import * as Yup from "yup";
import { useState, forwardRef, useImperativeHandle } from "react";
import { Icon } from "@iconify/react";
import { useFormik, Form, FormikProvider } from "formik";
import eyeFill from "@iconify/icons-eva/eye-fill";
import eyeOffFill from "@iconify/icons-eva/eye-off-fill";
import { useNavigate } from "react-router-dom";
// material
import { Stack, TextField, IconButton, InputAdornment } from "@mui/material";

import { useDispatch } from "react-redux";

import { register } from "src/redux/slices/Auth";
import { showAlert } from "../../../redux/slices/AlertSlice";
import { useTranslation } from 'react-i18next';
import { getRegisterSchema } from "src/utils/validationSchemas";
// ----------------------------------------------------------------------

const RegisterForm = forwardRef(({ setSubmitting }, ref) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const RegisterSchema = getRegisterSchema(t);

    const formik = useFormik({
        initialValues: {
            name: "",
            last_name: "",
            company_name: "",
            company_address: "",
            postcode_location: "",
            customer_number: "",
            email: "",
            password: "",
            re_password: "",
            phone: "",
        },
        validationSchema: RegisterSchema,
        onSubmit: (values, actions) => {
            setLoading(true);
            setSubmitting(true);
            const {
                name,
                last_name,
                company_name,
                company_address,
                postcode_location,
                customer_number,
                email,
                password,
                re_password,
                phone,
            } = values;
            dispatch(
                register({
                    t,
                    name,
                    last_name,
                    email,
                    password,
                    re_password,
                    customer_number,
                    company_name,
                    company_address,
                    postcode_location,
                    phone,
                })
            )
                .unwrap()
                .then(() => {
                    setLoading(false);
                    setSubmitting(false);
                    actions.setSubmitting(false);
                    dispatch(
                        showAlert({
                            message:
                                t('RegisterForm.success.message'),
                            severity: "success",
                        })
                    );
                    navigate("/dashboard", { replace: true });
                })
                .catch(() => {
                    setLoading(false);
                    setSubmitting(false);
                    actions.setSubmitting(false);
                });
        },
    });

    const { errors, touched, handleSubmit, isSubmitting, getFieldProps } =
        formik;

    useImperativeHandle(ref, () => ({
        submitForm() {
            formik.handleSubmit();
        },
    }));

    return (
        <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                <Stack spacing={2} sx={{ mb: 1 }}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <TextField
                            fullWidth
                            label={t('RegisterForm.username.placeholder')}
                            {...getFieldProps("name")}
                            error={Boolean(touched.name && errors.name)}
                            helperText={touched.name && errors.name}
                        />

                        <TextField
                            fullWidth
                            label={t('RegisterForm.lastName.placeholder')}
                            {...getFieldProps("last_name")}
                            error={Boolean(
                                touched.last_name && errors.last_name
                            )}
                            helperText={touched.last_name && errors.last_name}
                        />
                    </Stack>

                    <TextField
                        fullWidth
                        autoComplete="username"
                        type="email"
                        label={t('RegisterForm.email.placeholder')}
                        {...getFieldProps("email")}
                        error={Boolean(touched.email && errors.email)}
                        helperText={touched.email && errors.email}
                    />

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <TextField
                            fullWidth
                            autoComplete="current-password"
                            type={showPassword ? "text" : "password"}
                            label={t('RegisterForm.password.placeholder')}
                            {...getFieldProps("password")}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            edge="end"
                                            onClick={() =>
                                                setShowPassword((prev) => !prev)
                                            }
                                        >
                                            <Icon
                                                icon={
                                                    showPassword
                                                        ? eyeFill
                                                        : eyeOffFill
                                                }
                                            />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            error={Boolean(touched.password && errors.password)}
                            helperText={touched.password && errors.password}
                        />
                        <TextField
                            fullWidth
                            autoComplete="re_password"
                            type={showPassword ? "text" : "password"}
                            label={t('RegisterForm.rePassword.placeholder')}
                            {...getFieldProps("re_password")}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            edge="end"
                                            onClick={() =>
                                                setShowPassword((prev) => !prev)
                                            }
                                        >
                                            <Icon
                                                icon={
                                                    showPassword
                                                        ? eyeFill
                                                        : eyeOffFill
                                                }
                                            />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            error={Boolean(
                                touched.re_password && errors.re_password
                            )}
                            helperText={
                                touched.re_password && errors.re_password
                            }
                        />
                    </Stack>

                    <TextField
                        fullWidth
                        label={t('RegisterForm.companyName.placeholder')}
                        {...getFieldProps("company_name")}
                        error={Boolean(
                            touched.company_name && errors.company_name
                        )}
                        helperText={touched.company_name && errors.company_name}
                    />

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <TextField
                            fullWidth
                            label={t('RegisterForm.companyAddress.placeholder')}
                            {...getFieldProps("company_address")}
                            error={Boolean(
                                touched.company_address &&
                                    errors.company_address
                            )}
                            helperText={
                                touched.company_address &&
                                errors.company_address
                            }
                        />

                        <TextField
                            fullWidth
                            label={t('RegisterForm.postCodeLocation.placeholder')}
                            {...getFieldProps("postcode_location")}
                            error={Boolean(
                                touched.postcode_location &&
                                    errors.postcode_location
                            )}
                            helperText={
                                touched.postcode_location &&
                                errors.postcode_location
                            }
                        />
                    </Stack>

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <TextField
                            fullWidth
                            label={t('RegisterForm.customerNumber.placeholder')}
                            {...getFieldProps("customer_number")}
                            error={Boolean(
                                touched.customer_number &&
                                    errors.customer_number
                            )}
                            helperText={
                                touched.customer_number &&
                                errors.customer_number
                            }
                        />

                        <TextField
                            fullWidth
                            label={t('RegisterForm.phone.placeholder')}
                            {...getFieldProps("phone")}
                            error={Boolean(touched.phone && errors.phone)}
                            helperText={touched.phone && errors.phone}
                        />
                    </Stack>
                </Stack>
            </Form>
        </FormikProvider>
    );
});

export default RegisterForm;
